import type {
  ISubscriptionPlan,
  ISubscriptionPlanFeatures,
} from '../services/registrations/customers/types';

export const SUBSCRIPTION_PLAN_OPTIONS = [
  { slug: 'legacy', name: 'Legado' },
  { slug: 'trial', name: 'Trial' },
] as const;

const LEGACY_FEATURES: ISubscriptionPlanFeatures = {
  indicators: true,
  quotes: true,
  priceComparison: true,
  chat: true,
  myStation: true,
  ai: true,
  stock: true,
};

const TRIAL_FEATURES: ISubscriptionPlanFeatures = {
  indicators: true,
  quotes: false,
  priceComparison: false,
  chat: false,
  myStation: false,
  ai: false,
  stock: false,
};

const FEATURES_BY_SLUG: Record<string, ISubscriptionPlanFeatures> = {
  legacy: LEGACY_FEATURES,
  trial: TRIAL_FEATURES,
};

/** Normaliza slug para envio à API (sem aliases legados — planos vêm do catálogo). */
export const normalizeSubscriptionPlanSlug = (slug: string): string => slug.trim().toLowerCase();

export const getSubscriptionPlanSlug = (
  plan: ISubscriptionPlan | string | null | undefined
): string => {
  if (!plan) return '';
  const raw = typeof plan === 'string' ? plan : plan.slug;
  return normalizeSubscriptionPlanSlug(raw);
};

export const getSubscriptionPlanLabel = (
  plan: ISubscriptionPlan | string | null | undefined,
  labels: Record<string, string> = {}
): string => {
  if (!plan) return '';
  if (typeof plan === 'string') {
    const slug = normalizeSubscriptionPlanSlug(plan);
    return labels[slug] ?? labels[plan] ?? slug;
  }
  return plan.name || labels[plan.slug] || plan.slug;
};

export const getSubscriptionPlanFeatures = (
  plan: ISubscriptionPlan | string | null | undefined
): ISubscriptionPlanFeatures => {
  const empty: ISubscriptionPlanFeatures = {
    indicators: false,
    quotes: false,
    priceComparison: false,
    chat: false,
    myStation: false,
    ai: false,
    stock: false,
  };

  if (!plan) {
    return empty;
  }

  const slug = getSubscriptionPlanSlug(plan);
  const defaults = FEATURES_BY_SLUG[slug] ?? empty;

  if (typeof plan !== 'string' && plan.features) {
    const f = plan.features;
    return {
      indicators: f.indicators ?? defaults.indicators,
      quotes: f.quotes ?? defaults.quotes,
      priceComparison: f.priceComparison ?? defaults.priceComparison,
      chat: f.chat ?? defaults.chat,
      myStation: f.myStation ?? defaults.myStation,
      ai: f.ai ?? defaults.ai,
      stock: f.stock ?? defaults.stock,
    };
  }

  return defaults;
};

export const mergeSubscriptionPlanFeatures = (
  plans: Array<ISubscriptionPlan | string | null | undefined>
): ISubscriptionPlanFeatures => {
  return plans.reduce<ISubscriptionPlanFeatures>(
    (acc, plan) => {
      const features = getSubscriptionPlanFeatures(plan);
      return {
        indicators: acc.indicators || features.indicators,
        quotes: acc.quotes || features.quotes,
        priceComparison: acc.priceComparison || features.priceComparison,
        chat: acc.chat || features.chat,
        myStation: acc.myStation || features.myStation,
        ai: acc.ai || features.ai,
        stock: acc.stock || features.stock,
      };
    },
    {
      indicators: false,
      quotes: false,
      priceComparison: false,
      chat: false,
      myStation: false,
      ai: false,
      stock: false,
    }
  );
};
